import {
  Box,
  type BoxProps,
  Card,
  Divider,
  Flex,
  Skeleton,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreItem } from '@flecto/types'

import { useItemLowestPriceRule } from '../../../../../flecto-modules-saas/src'
import { useStoreDates } from '../../hooks/useStoreDates'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStoreItemAvailability } from '../../hooks/useStoreItemAvailability'
import { ButtonItemAddToCart } from '../ButtonItemAddToCart/ButtonItemAddToCart'
import { SelectItemQuantity } from '../SelectItemQuantity/SelectItemQuantity'
import { TextCurrencyStore } from '../TextCurrencyStore/TextCurrencyStore'
import { TextItemAvailability } from '../TextItemAvailability/TextItemAvailability'
import styles from './styles.module.css'

export type CardItemControlsProps = BoxProps & {
  item?: CoreItem
  isLoading?: boolean
}

export function CardItemControls({
  item,
  isLoading,
  ...props
}: CardItemControlsProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-page-product' })

  const store = useStoreDetails()
  const [quantityToAdd, setQuantityToAdd] = useState(1)
  const priceRule = useItemLowestPriceRule({ item })

  const { setDatesOpened, startAt, endAt } = useStoreDates()

  const { item: itemWithAvailability, isLoading: isLoadingItemAvailability } =
    useStoreItemAvailability({ item, quantityToAdd })

  if (isLoading) {
    return (
      <Card {...props} className={styles.itemControls}>
        <Skeleton h={20} w={150} radius='xl' />
        <Skeleton h={20} w={200} radius='xl' mt='sm' />
        <Box mt='xl' mb='lg'>
          <Skeleton h={10} w={50} mb='sm' />
          <Skeleton h={30} w={80} radius='xl' />
          <Skeleton h={10} w={150} mt='sm' mb='xl' />
        </Box>
        <Flex mt='lg' align='center' justify='space-between'>
          <Skeleton h={15} w={100} />
          <Skeleton h={15} w={80} />
        </Flex>
        <Skeleton h={60} mt='lg' />
      </Card>
    )
  }

  if (!item) {
    return
  }

  return (
    <Card {...props} className={styles.itemControls}>
      <Title order={4} fw={700}>
        {itemWithAvailability?.title}
      </Title>
      <Box mt='lg'>
        <Text c='dimmed'>{t('label_price_from')}</Text>
        <TextCurrencyStore
          cents={priceRule?.price_cents ?? 0}
          fw={400}
          lh={1.1}
          size='25'
        />
        {store?.show_prices && (
          <Tooltip
            label={t('label_price_estimation_tooltip')}
            withinPortal={true}
            position='top-start'
          >
            <Text mt='xs'>
              {t('label_price_estimation')}:
              {isLoadingItemAvailability ? (
                <Skeleton h={10} w={50} display='inline-block' ml='sm' />
              ) : startAt && endAt ? (
                <TextCurrencyStore
                  display='inline'
                  ml='sm'
                  cents={itemWithAvailability?.suggestedPrice}
                  fw={500}
                  style={{
                    cursor: 'default',
                    borderBottom: '1px dashed var(--mantine-color-gray-4)',
                  }}
                />
              ) : (
                <Box
                  display='inline'
                  fw={500}
                  ml='xs'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDatesOpened(true)}
                >
                  {t('label_price_estimation_select_dates')}
                </Box>
              )}
            </Text>
          </Tooltip>
        )}
      </Box>
      <Divider my='lg' color='gray.3' />
      <Flex mt='lg' align='center' justify='space-between'>
        <SelectItemQuantity value={quantityToAdd} onChange={setQuantityToAdd} />
        <TextItemAvailability
          size='sm'
          c='dimmed'
          item={itemWithAvailability}
          isLoading={isLoadingItemAvailability}
        />
      </Flex>
      <ButtonItemAddToCart
        fullWidth
        mt='lg'
        size='lg'
        item={itemWithAvailability}
        quantity={quantityToAdd}
      />
    </Card>
  )
}
